import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { sessionsKeys } from "@/features/sessions/queries/sessionsQueryKeys";
import bookingService, {
    type AdminSwapSessionResult,
    type SwapSessionInput,
} from "@/features/sessions/services/bookingService";

type Variables = SwapSessionInput & { sessionId: string };

export function useSwapSession() {
    const queryClient = useQueryClient();

    const mutation = useMutation<AdminSwapSessionResult, Error, Variables>({
        mutationFn: async ({ sessionId: _sessionId, ...input }) => {
            return await bookingService.adminSwapSession(input);
        },
        onError: (error) => {
            const message = error instanceof Error ? error.message : "Failed to swap session. Please try again.";
            toast.error(message);
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: sessionsKeys.attendance.session(variables.sessionId) });
            queryClient.invalidateQueries({ queryKey: sessionsKeys.sessions.detail(variables.sessionId) });
            queryClient.invalidateQueries({ queryKey: sessionsKeys.attendance.session(variables.newSessionId) });
            queryClient.invalidateQueries({ queryKey: sessionsKeys.sessions.detail(variables.newSessionId) });
            if (data.isOverbooked) {
                toast.success("Booking swapped — session is now overbooked");
                return;
            }
            toast.success("Booking swapped");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
